/**
 * AIChatPanel — AI chat interface with streaming, smart scroll, token buffering.
 *
 * Finish-flash fix (B1): when the SSE stream ends, the panel does NOT reload
 * the conversation into a fresh list. It quietly fetches the persisted rows
 * and reconciles them into the existing message array (reconcileMessages):
 * stable render keys, reused object references for untouched rows (the
 * renderer is memoized), no loading toggle, no scroll reset. The streaming
 * bubble and every message share ONE keyed children array, so the streamed
 * bubble morphs into its persisted row in a single React commit.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { Box, TextField, IconButton, Typography, CircularProgress, Paper, Chip, Fade, Tooltip, Snackbar, Button, Link } from '@mui/material';
import { Send, Stop, KeyboardArrowDown, ContentCopy, MenuOpen, ChevronLeft, AutoAwesome } from '@mui/icons-material';
import { MoftakiBotIcon } from '../../components/shared/MoftakiBotIcon';
import { alpha, useTheme, keyframes } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChatSidebar } from './ChatSidebar';
import { ChatMessageRenderer } from './ChatMessageRenderer';
import { ThinkingIndicator, ReasoningAccordion } from './ThinkingIndicator';
import { MoftakiWhatsNewDialog, shouldShowWhatsNew } from './MoftakiWhatsNewDialog';
import { ChatEmptyState, ChatIdeasDialog } from './ChatEmptyState';
import { reconcileMessages, modelShortName, dayKey, formatDayLabel } from './chatMessageUtils';
import * as chatApi from '../../services/chatService';
import type { ChatMessage, SSEEvent } from '../../services/chatService';

const MIN_SEND_INTERVAL_MS = 1000;

const fadeSlideIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

type ChatStatus = 'idle' | 'thinking' | 'streaming';

export const AIChatPanel = ({ fullHeight }: { fullHeight?: boolean } = {}) => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  // Detect base path — works for both /admin/chat and /moftakibot
  const basePath = location.pathname.startsWith('/admin/chat') ? '/admin/chat' : '/moftakibot';
  const [conversations, setConversations] = useState<chatApi.Conversation[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [convModel, setConvModel] = useState<string | null>(null);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [streamText, setStreamText] = useState('');
  const [thinkingStatus, setThinkingStatus] = useState<string | null>(null);
  // Streamed reasoning (Sonnet 5 adaptive thinking) — streaming-only, never persisted
  const [reasoningText, setReasoningText] = useState('');
  const [reasoningOpen, setReasoningOpen] = useState(false);
  const [reasoningLive, setReasoningLive] = useState(false);
  // Persistent error bubble — survives past the end of the stream
  const [errorText, setErrorText] = useState<string | null>(null);
  const [agentStats, setAgentStats] = useState<{ input: number; output: number; iteration: number; durationMs: number } | null>(null);
  const [loading, setLoading] = useState(false);
  const [showJumpBottom, setShowJumpBottom] = useState(false);
  const [lastSendTime, setLastSendTime] = useState(0);
  const [copiedSnack, setCopiedSnack] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [ideasOpen, setIdeasOpen] = useState(false);
  const [whatsNewOpen, setWhatsNewOpen] = useState(false);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const tokenBufferRef = useRef('');
  const rafRef = useRef<number | null>(null);
  const userNearBottomRef = useRef(true);
  // Full streamed text of the in-flight turn (mirror of streamText, sync-readable)
  const streamTextRef = useRef('');
  // Per-turn stable render key for the streaming bubble (stream-1, stream-2, …)
  const turnRef = useRef(0);
  const streamKeyRef = useRef('stream-0');

  // ── Smart auto-scroll ──
  const isNearBottom = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 150;
  }, []);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const handleScroll = useCallback(() => {
    const near = isNearBottom();
    userNearBottomRef.current = near;
    setShowJumpBottom(!near && messages.length > 0);
  }, [isNearBottom, messages.length]);

  useEffect(() => {
    if (userNearBottomRef.current) scrollToBottom();
  }, [messages, streamText, scrollToBottom]);

  // One-time "MoftakiAI got smarter" announcement
  useEffect(() => { if (shouldShowWhatsNew()) setWhatsNewOpen(true); }, []);

  // Sync conversation ID from URL on mount
  useEffect(() => {
    const match = location.pathname.match(/(?:\/admin\/chat|\/moftakibot)\/([a-f0-9-]+)/i);
    if (match) {
      const urlId = match[1];
      if (urlId !== activeId) loadConversation(urlId);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadConversations(); }, []);

  const focusInput = () => {
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const usePrompt = (text: string) => {
    setInput(text);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const loadConversations = async () => {
    try {
      const list = await chatApi.listConversations();
      setConversations(list);
    } catch (err) { console.error('Failed to load conversations', err); }
  };

  /** Full (re)load — used when SWITCHING conversations, never after a stream. */
  const loadConversation = async (id: string) => {
    const isSameConv = id === activeId;
    setActiveId(id);
    navigate(`${basePath}/${id}`, { replace: true });
    // Only show loading spinner when switching to a different conversation
    if (!isSameConv) setLoading(true);
    setThinkingStatus(null);
    if (!isSameConv) setErrorText(null);
    try {
      const data = await chatApi.getConversation(id);
      setMessages((prev) => (isSameConv ? reconcileMessages(prev, data.messages) : data.messages));
      setConvModel(data.conversation.Model || null);
      setAgentStats({
        input: data.conversation.TotalInputTokens,
        output: data.conversation.TotalOutputTokens,
        iteration: 0, durationMs: 0,
      });
    } catch (err) { console.error('Failed to load conversation', err); }
    setLoading(false);
    focusInput();
  };

  const handleNew = async () => {
    try {
      const id = await chatApi.createConversation();
      setActiveId(id);
      setMessages([]);
      setAgentStats(null);
      setThinkingStatus(null);
      setErrorText(null);
      navigate(`${basePath}/${id}`, { replace: true });
      await loadConversations();
      focusInput();
    } catch (err) { console.error('Failed to create conversation', err); }
  };

  const handleArchive = async (id: string) => {
    try {
      await chatApi.archiveConversation(id);
      if (activeId === id) { setActiveId(null); setMessages([]); navigate(basePath, { replace: true }); }
      await loadConversations();
    } catch (err) { console.error('Failed to archive', err); }
  };

  // ── 60fps token buffer flush ──
  const flushTokenBuffer = useCallback(() => {
    if (tokenBufferRef.current) {
      const chunk = tokenBufferRef.current;
      tokenBufferRef.current = '';
      streamTextRef.current += chunk;
      setStreamText((prev) => prev + chunk);
    }
    rafRef.current = null;
  }, []);

  const bufferToken = useCallback((token: string) => {
    tokenBufferRef.current += token;
    if (!rafRef.current) {
      rafRef.current = requestAnimationFrame(flushTokenBuffer);
    }
  }, [flushTokenBuffer]);

  const handleSend = async () => {
    if (!input.trim() || streaming) return;
    const now = Date.now();
    if (now - lastSendTime < MIN_SEND_INTERVAL_MS) return;
    setLastSendTime(now);

    let convId = activeId;
    if (!convId) {
      try {
        convId = await chatApi.createConversation();
        setActiveId(convId);
        navigate(`${basePath}/${convId}`, { replace: true });
        await loadConversations();
      } catch (err) { console.error('Failed to create conversation', err); return; }
    }

    turnRef.current += 1;
    const streamKey = `stream-${turnRef.current}`;
    streamKeyRef.current = streamKey;

    const userMsg: ChatMessage = {
      MessageKey: `temp-${Date.now()}`, ConversationKey: convId,
      Role: 'user', Content: input.trim(),
      ToolName: null, ToolInput: null, ToolOutput: null,
      InputTokens: 0, OutputTokens: 0, DurationMs: null,
      CreatedAt: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    // Keep the caret in the composer after send (esp. when send came from the
    // button click) so the user can immediately type the next message.
    focusInput();
    setStreaming(true);
    setStreamText('');
    setThinkingStatus('Thinking...');
    setReasoningText('');
    setReasoningOpen(false);
    setReasoningLive(false);
    setErrorText(null);
    tokenBufferRef.current = '';
    streamTextRef.current = '';
    const afterToolRef = { current: false }; // tracks if we just finished a tool call

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      await chatApi.sendMessageStream(convId, userMsg.Content!, (event: SSEEvent) => {
        switch (event.type) {
          case 'text':
            setThinkingStatus(null);
            // Answer is streaming — auto-collapse the reasoning accordion
            setReasoningOpen(false);
            setReasoningLive(false);
            // Insert paragraph break when new text starts after a tool execution
            if (afterToolRef.current) {
              bufferToken('\n\n');
              afterToolRef.current = false;
            }
            bufferToken(event.text || '');
            break;
          case 'thinking': setThinkingStatus(event.status || null); break;
          case 'thinking_block_start':
            setThinkingStatus(null);
            setReasoningLive(true);
            break;
          case 'thinking_text':
            setReasoningText((prev) => prev + (event.text || ''));
            break;
          case 'thinking_block_stop':
            setReasoningLive(false);
            break;
          case 'tool_start': setThinkingStatus(`Calling ${event.toolName}...`); break;
          case 'tool_executing': setThinkingStatus(`Running ${event.toolName}...`); break;
          case 'tool_result': setThinkingStatus('Analyzing results...'); afterToolRef.current = true; break;
          case 'agent_stats':
            setAgentStats({ input: event.inputTokens || 0, output: event.outputTokens || 0, iteration: event.iteration || 0, durationMs: event.durationMs || 0 });
            break;
          case 'title': loadConversations(); break;
          case 'done':
            if (tokenBufferRef.current) {
              const tail = tokenBufferRef.current;
              tokenBufferRef.current = '';
              streamTextRef.current += tail;
              setStreamText((prev) => prev + tail);
            }
            if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
            setAgentStats({ input: event.inputTokens || 0, output: event.outputTokens || 0, iteration: event.iteration || 0, durationMs: event.durationMs || 0 });
            // Return focus to the composer when the answer finishes so the user
            // can keep the conversation going without clicking back in.
            focusInput();
            break;
          case 'error':
            setErrorText(event.message || 'The AI request failed.');
            setThinkingStatus(null);
            break;
        }
      }, controller.signal);
    } catch (err: unknown) {
      const e = err as { name?: string; message?: string };
      if (e.name !== 'AbortError') setErrorText(e.message || 'The AI request failed.');
    }

    // ── Flash-free finish (B1) ──
    // Fetch the persisted rows QUIETLY while the streamed bubble is still on
    // screen, then reconcile + clear the stream state in ONE batched commit:
    // the streamed bubble (key stream-N) becomes the persisted row (same key),
    // untouched rows keep their object references, nothing unmounts, and the
    // scroll position is left alone.
    if (convId) {
      try {
        const data = await chatApi.getConversation(convId);
        setMessages((prev) => reconcileMessages(prev, data.messages, {
          streamKey, streamText: streamTextRef.current,
        }));
        setConvModel(data.conversation.Model || null);
        setAgentStats((prevStats) => ({
          input: data.conversation.TotalInputTokens,
          output: data.conversation.TotalOutputTokens,
          iteration: 0,
          durationMs: prevStats?.durationMs ?? 0,
        }));
        loadConversations(); // refresh sidebar counts in the background
      } catch {
        // Reconcile fetch failed — materialize the streamed text locally so the
        // answer does not vanish; the next conversation load trues it up.
        if (streamTextRef.current) {
          const localRow: ChatMessage = {
            MessageKey: streamKey, ConversationKey: convId, Role: 'assistant',
            Content: streamTextRef.current, ToolName: null, ToolInput: null, ToolOutput: null,
            InputTokens: 0, OutputTokens: 0, DurationMs: null, CreatedAt: new Date().toISOString(),
          };
          setMessages((prev) => [...prev, localRow]);
        }
      }
    }
    setStreaming(false);
    setStreamText('');
    setReasoningText('');
    setThinkingStatus(null);
    abortRef.current = null;
  };

  const handleStop = () => { abortRef.current?.abort(); setStreaming(false); setThinkingStatus(null); };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const cooldownActive = Date.now() - lastSendTime < MIN_SEND_INTERVAL_MS;
  const status: ChatStatus = streaming ? (streamText ? 'streaming' : 'thinking') : 'idle';
  const modelShort = modelShortName(convModel);

  // ── ONE keyed children array: messages + day dividers + streaming bubble ──
  // Everything shares the same array so the streamed bubble's key survives the
  // reconcile (no unmount when it becomes a persisted row).
  const listItems: React.ReactNode[] = [];
  let lastDay = '';
  for (const m of messages) {
    const day = dayKey(m.CreatedAt);
    if (day && day !== lastDay) {
      lastDay = day;
      listItems.push(<DayDivider key={`day-${day}`} label={formatDayLabel(m.CreatedAt)} />);
    }
    listItems.push(<ChatMessageRenderer key={m.MessageKey} message={m} modelShort={modelShort} />);
  }
  if (streaming && reasoningText) {
    listItems.push(
      <ReasoningAccordion key="reasoning" text={reasoningText} open={reasoningOpen}
        onToggle={() => setReasoningOpen((o) => !o)} live={reasoningLive} />,
    );
  }
  if (streaming && streamText) {
    listItems.push(
      <ChatMessageRenderer key={streamKeyRef.current} isStreaming modelShort={modelShort} message={{
        MessageKey: streamKeyRef.current, ConversationKey: activeId || '', Role: 'assistant', Content: streamText,
        ToolName: null, ToolInput: null, ToolOutput: null, InputTokens: 0, OutputTokens: 0, DurationMs: null,
        CreatedAt: new Date().toISOString(),
      }} />,
    );
  }
  if (streaming && thinkingStatus) {
    listItems.push(<ThinkingIndicator key="thinking" status={thinkingStatus} />);
  }

  return (
    <Paper variant="outlined" sx={{
      display: 'flex', height: fullHeight ? '100%' : 'calc(100vh - 140px)',
      borderRadius: 2, overflow: 'hidden',
      borderColor: alpha(theme.palette.divider, 0.5),
    }}>
      {/* Collapsible sidebar */}
      <Box sx={{
        width: sidebarOpen ? 260 : 0, flexShrink: 0, overflow: 'hidden',
        transition: 'width 0.25s ease-in-out', height: '100%',
      }}>
        <Box sx={{ width: 260, height: '100%' }}>
          <ChatSidebar conversations={conversations} activeId={activeId}
            onSelect={loadConversation} onNew={handleNew} onArchive={handleArchive} />
        </Box>
      </Box>

      {/* Main chat area — key={activeId} forces remount with fade animation on conversation switch */}
      <Box key={activeId || 'empty'} sx={{
        flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0,
        backgroundColor: theme.palette.background.paper,
        animation: `${fadeSlideIn} 0.3s ease-out`,
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
      }}>
        {/* Header */}
        <Box sx={{
          px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
          backgroundColor: theme.palette.grey[50],
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <Tooltip title={sidebarOpen ? 'Hide conversations' : 'Show conversations'}>
              <IconButton size="small" onClick={() => setSidebarOpen((o) => !o)} sx={{ mr: 0.5 }}>
                {sidebarOpen ? <ChevronLeft sx={{ fontSize: 18 }} /> : <MenuOpen sx={{ fontSize: 18 }} />}
              </IconButton>
            </Tooltip>
            <MoftakiBotIcon size={22} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
              Moftaki AI
            </Typography>
            {/* Model chip + live status dot */}
            <Chip size="small"
              icon={<StatusDot status={status} />}
              label={modelShort}
              sx={{
                height: 20, fontSize: '0.62rem', fontWeight: 600,
                backgroundColor: alpha(theme.chat.botAccent, 0.08), color: theme.chat.botAccent,
                '& .MuiChip-icon': { ml: 0.75, mr: -0.25 },
              }} />
            {activeId && (
              <Tooltip title={`Copy session ID: ${activeId.substring(0, 8)}...`}>
                <IconButton size="small" onClick={() => { navigator.clipboard.writeText(activeId); setCopiedSnack(true); }}
                  sx={{ ml: 0.5, opacity: 0.5, '&:hover': { opacity: 1 } }}>
                  <ContentCopy sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            )}
            <Link component="button" variant="caption" onClick={() => setWhatsNewOpen(true)}
              sx={{ fontSize: '0.65rem', color: 'text.secondary', textDecorationColor: alpha(theme.palette.text.secondary, 0.3), flexShrink: 0 }}>
              What's new
            </Link>
          </Box>
          {agentStats && (
            <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
              {streaming && agentStats.iteration > 0 && (
                <Chip size="small" variant="outlined" color="warning" label={`Iter ${agentStats.iteration}`} sx={{ height: 20, fontSize: '0.65rem' }} />
              )}
              <Chip size="small" variant="outlined"
                label={`${agentStats.input.toLocaleString()} in / ${agentStats.output.toLocaleString()} out`}
                sx={{ height: 22, fontSize: '0.68rem' }} />
            </Box>
          )}
        </Box>

        {/* Messages */}
        <Box ref={scrollContainerRef} onScroll={handleScroll}
          sx={{ flex: 1, overflow: 'auto', py: 2, position: 'relative', backgroundColor: theme.palette.background.paper }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
          ) : messages.length === 0 && !streaming && !errorText ? (
            <ChatEmptyState onNew={activeId ? undefined : handleNew} onSuggestionClick={usePrompt} />
          ) : (
            <>
              {listItems}
              {errorText && !streaming && (
                <Box sx={{ px: 2, py: 1 }}>
                  <Paper variant="outlined" sx={{
                    px: 2, py: 1.25, maxWidth: '85%', borderRadius: 2,
                    borderColor: alpha(theme.palette.error.main, 0.5),
                    backgroundColor: alpha(theme.palette.error.main, 0.05),
                  }}>
                    <Typography variant="body2" sx={{ fontSize: '0.82rem', color: theme.palette.error.dark, fontWeight: 600, mb: 0.25 }}>
                      The AI could not answer
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.primary' }}>
                      {errorText}
                    </Typography>
                  </Paper>
                </Box>
              )}
              <div ref={messagesEndRef} />
            </>
          )}

          <Fade in={showJumpBottom}>
            <Box onClick={scrollToBottom} sx={{
              position: 'sticky', bottom: 8, display: 'flex', justifyContent: 'center',
              pointerEvents: showJumpBottom ? 'auto' : 'none',
            }}>
              <Chip icon={<KeyboardArrowDown />} label="Jump to bottom" size="small" clickable
                sx={{
                  backgroundColor: alpha(theme.palette.primary.main, 0.9), color: theme.palette.primary.contrastText,
                  fontSize: '0.7rem', height: 26,
                  '& .MuiChip-icon': { color: theme.palette.primary.contrastText, fontSize: 16 },
                  boxShadow: 3,
                }} />
            </Box>
          </Fade>
        </Box>

        {/* Input */}
        <Box sx={{ p: 1.5, borderTop: `1px solid ${alpha(theme.palette.divider, 0.3)}`, backgroundColor: theme.palette.grey[50] }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
            {/* Not disabled while streaming: keep the input focusable so the user
                can keep typing (send is guarded in handleSend + the Enter handler);
                disabling here dropped browser focus and forced a re-click. */}
            <TextField fullWidth multiline maxRows={6} size="small" inputRef={inputRef}
              placeholder={activeId ? 'Ask about your lab data...' : 'Start a new chat...'}
              value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '0.85rem', backgroundColor: theme.palette.background.paper } }} />
            {streaming ? (
              <Tooltip title="Stop generating">
                <IconButton onClick={handleStop} color="error" size="small" sx={{ mb: 0.5 }}><Stop /></IconButton>
              </Tooltip>
            ) : (
              <IconButton onClick={handleSend} color="primary" size="small" disabled={!input.trim() || cooldownActive} sx={{ mb: 0.5 }}><Send /></IconButton>
            )}
          </Box>
          <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
              Enter to send · Shift+Enter for a new line · AI can make mistakes, verify important data.
            </Typography>
            {messages.length > 0 && (
              <Button size="small" startIcon={<AutoAwesome sx={{ fontSize: '13px !important' }} />}
                onClick={() => setIdeasOpen(true)}
                sx={{ fontSize: '0.65rem', textTransform: 'none', py: 0, px: 0.75, minWidth: 0, color: theme.chat.botAccent, flexShrink: 0 }}>
                Ideas
              </Button>
            )}
          </Box>
        </Box>
      </Box>
      <Snackbar open={copiedSnack} autoHideDuration={2000} onClose={() => setCopiedSnack(false)}
        message="Session ID copied to clipboard" anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
      <MoftakiWhatsNewDialog open={whatsNewOpen} onClose={() => setWhatsNewOpen(false)} onUsePrompt={usePrompt} />
      <ChatIdeasDialog open={ideasOpen} onClose={() => setIdeasOpen(false)} onPick={usePrompt} />
    </Paper>
  );
};

/** Small pulsing status dot: grey idle · amber thinking · green streaming. */
function StatusDot({ status }: { status: ChatStatus }) {
  const theme = useTheme();
  const color = status === 'streaming' ? theme.customColors.statusActive
    : status === 'thinking' ? theme.schedule.maintenance
      : theme.schedule.noActivity;
  return (
    <Box component="span" sx={{
      width: 7, height: 7, borderRadius: '50%', backgroundColor: color, display: 'inline-block',
      ...(status !== 'idle' && {
        '@keyframes status-pulse': {
          '0%, 100%': { boxShadow: `0 0 0 0 ${alpha(color, 0.4)}` },
          '50%': { boxShadow: `0 0 0 4px ${alpha(color, 0)}` },
        },
        animation: 'status-pulse 1.6s ease-in-out infinite',
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
      }),
    }} />
  );
}

/** Centered "Today" / "Jul 11" divider between calendar days. */
function DayDivider({ label }: { label: string }) {
  const theme = useTheme();
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 3, my: 1.5 }}>
      <Box sx={{ flex: 1, height: '1px', backgroundColor: alpha(theme.palette.divider, 0.5) }} />
      <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary', fontWeight: 600 }}>
        {label}
      </Typography>
      <Box sx={{ flex: 1, height: '1px', backgroundColor: alpha(theme.palette.divider, 0.5) }} />
    </Box>
  );
}
